import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Sparkles } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const AiChatWidget: React.FC = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [waSending, setWaSending] = useState(false);
  const [waToast, setWaToast] = useState<{msg: string, ok: boolean} | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // גלילה לתחתית כשיש הודעות חדשות
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // טעינת היסטוריה בפתיחת הוידג'ט
  useEffect(() => {
    if (isOpen && !historyLoaded) {
      loadHistory();
    }
    if (isOpen) {
      setUnreadCount(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, historyLoaded]);

  const loadHistory = async () => {
    try {
      const res = await api.get('/ai/history');
      const historyMsgs: ChatMessage[] = (res.data.messages || []).map((m: { role: 'user' | 'assistant'; content: string; timestamp: string }) => ({
        role: m.role,
        content: m.content,
        timestamp: new Date(m.timestamp),
      }));

      if (historyMsgs.length === 0) {
        // הודעת פתיחה אוטומטית
        const orgName = 'Amutech'; // ברירת מחדל — ניתן להרחיב עם context ארגון
        const greeting: ChatMessage = {
          role: 'assistant',
          content: `שלום ${user?.name || 'משתמש'}! אני העוזר של ${orgName}. כיצד אוכל לעזור?`,
          timestamp: new Date(),
        };
        setMessages([greeting]);
      } else {
        setMessages(historyMsgs);
      }

      setHistoryLoaded(true);
    } catch {
      // אם נכשל — פשוט הצג הודעת פתיחה
      const greeting: ChatMessage = {
        role: 'assistant',
        content: `שלום ${user?.name || 'משתמש'}! אני העוזר של Amutech. כיצד אוכל לעזור?`,
        timestamp: new Date(),
      };
      setMessages([greeting]);
      setHistoryLoaded(true);
    }
  };

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    const userMsg: ChatMessage = {
      role: 'user',
      content: trimmed,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      const res = await api.post('/ai/chat', { message: trimmed });
      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: res.data.reply,
        timestamp: new Date(res.data.timestamp),
      };
      setMessages((prev) => [...prev, assistantMsg]);

      // אם החלון סגור — הגדל badge
      if (!isOpen) {
        setUnreadCount((c) => c + 1);
      }
    } catch {
      const errorMsg: ChatMessage = {
        role: 'assistant',
        content: 'מצטער, אירעה שגיאה. אנא נסה שוב.',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div
      dir="rtl"
      style={{ position: 'fixed', bottom: '24px', left: '24px', zIndex: 9999 }}
    >
      {/* Chat Window */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            bottom: '70px',
            left: '0',
            width: '380px',
            height: '520px',
            display: 'flex',
            flexDirection: 'column',
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            overflow: 'hidden',
            border: '1px solid #e0e0e0',
          }}
          className="dark:bg-[#1E1E1E] dark:border-[#2C2C2C]"
        >
          {/* Header */}
          <div
            style={{
              background: 'linear-gradient(135deg, #1565C0 0%, #7B1FA2 100%)',
              padding: '14px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                }}
              >
                ✨
              </div>
              <div>
                <div style={{ color: 'white', fontWeight: 700, fontSize: '15px', fontFamily: 'inherit' }}>
                  Amutech עוזר
                </div>
                <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '12px' }}>
                  AI • מוכן לעזור
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                onClick={async () => {
                  if (waSending) return;
                  try {
                    setWaSending(true);
                    const meRes = await api.get('/auth/me');
                    const phone = meRes.data?.phone;
                    if (!phone) {
                      setWaToast({ msg: '⚠️ הוסף טלפון בפרופיל שלך', ok: false });
                      setTimeout(() => setWaToast(null), 4000);
                      setWaSending(false);
                      return;
                    }
                    await api.post('/whatsapp/send-me', { message: `שלום ${meRes.data.name || ''}! 🐝 העוזר של Amutech כאן — במה אוכל לעזור?` });
                    setWaToast({ msg: '✅ הודעה נשלחה ב-WhatsApp!', ok: true });
                    setTimeout(() => setWaToast(null), 3000);
                  } catch {
                    setWaToast({ msg: '❌ שגיאה בשליחה', ok: false });
                    setTimeout(() => setWaToast(null), 3000);
                  } finally {
                    setWaSending(false);
                  }
                }}
                style={{
                  background: '#25D366', border: 'none', borderRadius: '8px',
                  padding: '5px 10px', cursor: waSending ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '4px',
                  color: 'white', fontSize: '11px', fontWeight: 700, fontFamily: 'inherit',
                  opacity: waSending ? 0.7 : 1, transition: 'all 0.2s',
                }}
                title="בוא נעבור לוואטסאפ"
              >
                💬 {waSending ? '...' : 'וואטסאפ'}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'white',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.25)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.15)')}
                aria-label="סגור"
              >
              <X size={18} />
            </button>
            </div>
          </div>

          {/* Messages Area */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              background: '#FAFAFA',
            }}
            className="dark:bg-[#121212]"
          >
            {messages.map((msg, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <div
                  style={{
                    maxWidth: '82%',
                    padding: '10px 14px',
                    borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    background: msg.role === 'user'
                      ? 'linear-gradient(135deg, #1565C0, #1976D2)'
                      : '#EEEEEE',
                    color: msg.role === 'user' ? 'white' : '#212121',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  }}
                  className={msg.role === 'assistant' ? 'dark:bg-[#2C2C2C] dark:text-[#E0E0E0]' : ''}
                >
                  {msg.content}
                </div>
                <span
                  style={{
                    fontSize: '11px',
                    color: '#9E9E9E',
                    marginTop: '3px',
                    direction: 'ltr',
                  }}
                >
                  {formatTime(msg.timestamp)}
                </span>
              </div>
            ))}

            {/* Loading indicator — 3 נקודות מונפשות */}
            {isLoading && (
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <div
                  style={{
                    background: '#EEEEEE',
                    borderRadius: '18px 18px 18px 4px',
                    padding: '12px 16px',
                    display: 'flex',
                    gap: '5px',
                    alignItems: 'center',
                  }}
                  className="dark:bg-[#2C2C2C]"
                >
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      style={{
                        width: '7px',
                        height: '7px',
                        borderRadius: '50%',
                        background: '#9E9E9E',
                        animation: `aiDotBounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div
            style={{
              padding: '12px 14px',
              borderTop: '1px solid #E0E0E0',
              background: 'white',
              display: 'flex',
              gap: '8px',
              alignItems: 'flex-end',
              flexShrink: 0,
            }}
            className="dark:bg-[#1E1E1E] dark:border-[#2C2C2C]"
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="כתוב הודעה... (Enter לשליחה)"
              disabled={isLoading}
              rows={1}
              style={{
                flex: 1,
                border: '1.5px solid #E0E0E0',
                borderRadius: '12px',
                padding: '10px 13px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'none',
                outline: 'none',
                background: '#FAFAFA',
                color: '#212121',
                transition: 'border-color 0.2s',
                maxHeight: '100px',
                overflowY: 'auto',
                direction: 'rtl',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = '#1565C0')}
              onBlur={(e) => (e.currentTarget.style.borderColor = '#E0E0E0')}
            />
            <button
              onClick={sendMessage}
              disabled={isLoading || !input.trim()}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                border: 'none',
                cursor: isLoading || !input.trim() ? 'not-allowed' : 'pointer',
                background: isLoading || !input.trim()
                  ? '#BDBDBD'
                  : 'linear-gradient(135deg, #1565C0, #7B1FA2)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                transition: 'all 0.2s',
                transform: isLoading || !input.trim() ? 'none' : 'scale(1)',
              }}
              onMouseEnter={(e) => {
                if (!isLoading && input.trim()) {
                  e.currentTarget.style.transform = 'scale(1.08)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
              aria-label="שלח"
            >
              <Send size={17} />
            </button>
          </div>
        </div>
      )}

      {/* WhatsApp toast */}
      {waToast && (
        <div style={{
          position: 'absolute', bottom: '130px', left: '0', right: '0',
          background: waToast.ok ? '#25D366' : '#ef4444',
          color: '#fff', borderRadius: '10px', padding: '8px 14px',
          fontSize: '13px', fontWeight: 600, textAlign: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,.2)', whiteSpace: 'nowrap',
        }}>
          {waToast.msg}
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen((o) => !o)}
        style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          border: 'none',
          cursor: 'pointer',
          background: isOpen
            ? 'linear-gradient(135deg, #7B1FA2, #1565C0)'
            : 'linear-gradient(135deg, #1565C0, #7B1FA2)',
          color: 'white',
          fontSize: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(21, 101, 192, 0.4)',
          position: 'relative',
          transition: 'all 0.3s',
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(21, 101, 192, 0.55)';
          e.currentTarget.style.transform = isOpen ? 'rotate(180deg) scale(1.08)' : 'scale(1.08)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(21, 101, 192, 0.4)';
          e.currentTarget.style.transform = isOpen ? 'rotate(180deg)' : 'rotate(0deg)';
        }}
        aria-label={isOpen ? 'סגור עוזר AI' : 'פתח עוזר AI'}
      >
        {isOpen ? <X size={22} /> : <Sparkles size={22} />}

        {/* Badge הודעות לא נקראות */}
        {!isOpen && unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              background: '#E53935',
              color: 'white',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              fontSize: '11px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid white',
              boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
            }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* CSS Animation for loading dots */}
      <style>{`
        @keyframes aiDotBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-6px); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default AiChatWidget;
